import React, { useState } from "react";
import { Link } from 'react-router-dom';
import { Shield, Building2, FileSpreadsheet, Check, X, Play } from 'lucide-react';

const LandingPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900">PDF STATEMENT CONVERTER</span>
              <span className="text-xs text-gray-500">Boone & Boo</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <a href="#pricing" className="text-gray-600 hover:text-gray-900">
              Pricing
            </a>
            <Link to="/blog" className="text-gray-600 hover:text-gray-900">
              Blog
            </Link>
            <Link to="/login" className="text-gray-600 hover:text-gray-900">
              Login
            </Link>
            <Link to="/register"className="text-gray-600 hover:text-gray-900">
              Register
            </Link>
            <button 
              onClick={openContactModal}
              className="text-gray-600 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 transition-colors"
            >
              Contact
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Convert SA Bank Statements to Accounting-Ready CSV
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Easily convert PDF bank statements from all major South African banks into formatted CSV files ready for Sage, QuickBooks, Xero, and other accounting software, for OFX and QBO conversions checkout{' '}
            <a href="https://pdfstatementconverter.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
              pdfstatementconverter.net
            </a>
          </p>

          {/* Video Demo Link */}
          <div className="mb-12">
            <a 
              href="https://www.youtube.com/watch?v=dqELsOuyUT8" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center mr-3 group-hover:bg-indigo-700 transition-colors">
                <Play className="w-4 h-4 text-white ml-0.5" />
              </div>
              <span className="font-semibold">Watch How It Works</span>
            </a>
          </div>

          {/* CTA Section */}
          <div className="bg-gray-50 rounded-2xl p-12 mb-16">
          <Link to="/login">
            <button
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-6 text-lg font-semibold rounded-xl"
            >
              Click here to convert a PDF!
            </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Privacy First</h3>
              <p className="text-gray-600">Your PDFs are processed instantly and never stored. Only transaction data is kept as CSV files.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">SA Banks</h3>
              <p className="text-gray-600">Supports FNB, Standard Bank, ABSA, Nedbank, Capitec and more</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Accounting Ready</h3>
              <p className="text-gray-600">
                Formatted CSV files ready to upload to accounting software like Sage, QuickBooks, Xero, Pastel, and more
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Choose Your Plan</h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Pay Per Use */}
            <div className="border-2 border-gray-200 hover:border-indigo-300 transition-colors rounded-lg overflow-hidden">
              <div className="text-center p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">Pay Per Use</h3>
                
                <div className="mt-4">
                  <span className="text-3xl font-bold text-gray-900">R2</span>
                  <span className="text-gray-600"> per 30 converted<br /> transactions</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Instant conversion</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600"> Registration required</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Secure processing</span>
                </div>
                <Link to="/login">
                  <button className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md">
                    Start Converting
                  </button>
                </Link>
              </div>
            </div>

            {/* Credit Packs */}
            <div className="border-2 border-indigo-300 hover:border-indigo-400 transition-colors rounded-lg overflow-hidden relative pt-4">
              <div className="absolute -top-6S left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md z-10">
                Best Value
              </div>
              <div className="text-center p-6 pt-8 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">Credit Packs</h3>
                <p className="text-gray-600 text-sm">Save with bulk credits</p>
                <div className="mt-4 space-y-2">
                  <div>
                    <span className="text-lg font-semibold text-gray-900">25 credits: R40</span>
                    <span className="text-sm text-gray-600 block">(R1.60 per page)</span>
                  </div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900">100 credits: R140</span>
                    <span className="text-sm text-gray-600 block">(R1.40 per page)</span>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded p-2 border border-orange-200">
                    <span className="text-lg font-bold text-orange-800">500 credits: R500</span>
                    <span className="text-sm text-orange-700 block">(R1.00 per page - Best Value!)</span>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Credits never expire</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Priority processing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Bulk discounts</span>
                </div>
                <Link to="/login">
                  <button className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md">
                    Buy Credits
                  </button>
                </Link>
              </div>
            </div>

            {/* Business */}
            <div className="border-2 border-gray-200 hover:border-emerald-300 transition-colors rounded-lg overflow-hidden">
              <div className="text-center p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">Business</h3>
                <p className="text-gray-600 text-sm">High volume processing</p>
                <div className="mt-4">
                  <span className="text-2xl font-bold text-gray-900">Custom</span>
                  <span className="text-gray-600 block text-sm">pricing</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Volume discounts</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Customisation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">Dedicated support</span>
                </div>
                <button 
                  onClick={openContactModal}
                  className="w-full mt-6 border border-emerald-600 text-emerald-600 hover:bg-emerald-50 py-2 px-4 rounded-md"
                >
                  Contact Sales
                </button>
              </div>
            </div>
          </div>

          {/* Promo Code CTA */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Have a promo code?{' '}
              <Link to="/register" className="text-indigo-600 hover:text-indigo-500 font-medium">
                Sign up here to use it!
              </Link>
            </p>
            <div className="text-sm text-gray-500">
              Get bonus credits when you register with a valid promo code
            </div>
          </div>
        </div>
      </section>

 

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">PDF Statement Converter</span>
          </div>
          <p className="text-gray-600 mb-4">Convert South African bank statements to accounting-ready CSV format</p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-gray-700">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-gray-700">
              Terms of Service
            </Link>
            <button onClick={openContactModal} className="hover:text-gray-700">
              Contact
            </button>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeContactModal}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeContactModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h2>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-gray-600 mb-2">Email us at:</p>
                <a 
                  href="mailto:admin@pdfstatementconverter.co.za"
                  className="text-indigo-600 hover:text-indigo-800 font-semibold text-lg"
                >
                  admin@pdfstatementconverter.co.za
                </a>
              </div>
              <button 
                onClick={closeContactModal}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;